import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { IKeepInTouchScore } from '../../../../../models/KeepInTouchAgent';
import { useSingleEmailComposer, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { createRawRichTextContentStateWithHtmlStringValue } from '../../../../../extViewmodels/sdk/Utils';
import {
	invalidateKeepInTouchScores,
	useAcknowledgeKeepInTouchScore,
	useUpdateKeepInTouchScore,
} from '../../../../../queries/KeepInTouchAgent';
import { EmailMessageViewModel } from '../../../../../viewmodels/AppViewModels';
import { StreamdownLite } from '../../../../components-v2/ai/StreamdownLite';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { TextArea } from '../../../../components-v2/TextArea';
import { useContactsPageAppDrawerQueryParamState } from '../../PeopleContainer/hooks/useContactsPageAppDrawerQueryParamState';
import { ScoreIndicator } from './ScoreIndicator';
import { styleSheet } from './styles';

/** Strip <cite> tags from AI-generated text */
function stripCiteTags(text: string): string {
	return text.replace(/<cite[^>]*>|<\/cite>/g, '');
}

interface ScoreCardProps {
	score: IKeepInTouchScore;
	configId: string;
	showSuppressed?: boolean;
}

export const ScoreCard: React.FC<ScoreCardProps> = ({ score, configId, showSuppressed = false }) => {
	const [expanded, setExpanded] = React.useState(false);
	const [hidden, setHidden] = React.useState(false);
	const [notes, setNotes] = React.useState(score.notes ?? '');
	const [notesDirty, setNotesDirty] = React.useState(false);

	React.useEffect(() => {
		if (!notesDirty) {
			setNotes(score.notes ?? '');
		}
	}, [score.notes, notesDirty]);

	React.useEffect(() => {
		if (showSuppressed && score.skip) {
			setHidden(false);
		}
	}, [showSuppressed, score.skip]);
	const userSession = useUserSession();
	const singleEmailComposer = useSingleEmailComposer();
	const { setDrawerToContactInfo } = useContactsPageAppDrawerQueryParamState();

	const handleOpenContactProfile = (e: React.MouseEvent) => {
		e.stopPropagation();
		setDrawerToContactInfo(score.contactId);
	};

	const suppressNoteSavedToast = React.useRef(false);
	const updateMutation = useUpdateKeepInTouchScore({
		onSuccess: () => {
			invalidateKeepInTouchScores(configId);
			setNotesDirty(false);
			if (!suppressNoteSavedToast.current) {
				toast.success('Note saved');
			}
			suppressNoteSavedToast.current = false;
		},
		onError: () => {
			toast.error('Failed to update score');
			suppressNoteSavedToast.current = false;
		},
	});

	const acknowledgeMutation = useAcknowledgeKeepInTouchScore({
		onSuccess: () => {
			setHidden(true);
			invalidateKeepInTouchScores(configId);
			toast.success(`${score.contactName ?? 'Contact'} snoozed — they'll reappear after the next run`);
		},
		onError: () => {
			toast.error('Failed to snooze score');
		},
	});

	const handleSkip = () => {
		suppressNoteSavedToast.current = true;
		updateMutation.mutate({
			scoreId: score.id,
			request: { notes: score.notes ?? '', skip: true },
		});
		if (!showSuppressed) setHidden(true);
		toast.success(`${score.contactName ?? 'Contact'} won't be suggested again for this job`);
	};

	const handleUnsuppress = () => {
		suppressNoteSavedToast.current = true;
		updateMutation.mutate({
			scoreId: score.id,
			request: { notes: score.notes ?? '', skip: false },
		});
		toast.success(`${score.contactName ?? 'Contact'} will be included in future runs`);
	};

	const handleSaveNotes = () => {
		updateMutation.mutate({
			scoreId: score.id,
			request: { notes, skip: score.skip },
		});
	};

	const handleSendEmail = () => {
		const contactVm = new Api.ContactViewModel(userSession, { id: score.contactId } as Api.IContact);
		const emailMessage = new EmailMessageViewModel<File>(userSession, true);

		emailMessage.contactsToAdd.add(contactVm);
		emailMessage.options.sendSingleEmail = true;

		if (score.suggestedOutreach) {
			let outreachText = stripCiteTags(score.suggestedOutreach);

			// Strip markdown formatting first
			outreachText = outreachText
				.replace(/\*\*(.*?)\*\*/g, '$1')
				.replace(/\*(.*?)\*/g, '$1')
				.replace(/\[(.*?)\]\(.*?\)/g, '$1');

			// Extract and remove subject line (e.g., "Subject: ...", "SUBJECT: ...", etc.)
			const lines = outreachText.split('\n');
			const subjectLineIndex = lines.findIndex(line => /^\s*subject\s*:/i.test(line));
			if (subjectLineIndex !== -1) {
				emailMessage.subject = lines[subjectLineIndex].replace(/^\s*subject\s*:\s*/i, '').trim();
				lines.splice(subjectLineIndex, 1);
			}

			// Remove leading empty lines after subject extraction
			while (lines.length > 0 && lines[0].trim() === '') {
				lines.shift();
			}

			// Collapse consecutive empty lines into a single break, join with <br>
			const htmlBody = lines
				.filter((line, i) => !(line.trim() === '' && i > 0 && lines[i - 1].trim() === ''))
				.join('<br>');
			emailMessage.content = createRawRichTextContentStateWithHtmlStringValue(htmlBody);
		}

		singleEmailComposer.showWithEmailMessage(emailMessage, [contactVm], (didSend: boolean) => {
			if (didSend) {
				acknowledgeMutation.mutate(score.id);
			}
		});
	};

	const daysText = score.daysSinceLastContact != null ? `${score.daysSinceLastContact}d since last contact` : null;

	if (hidden) return null;
	if (score.skip && !showSuppressed) return null;

	const isSuppressed = score.skip;

	return (
		<div className={css(styleSheet.card, isSuppressed ? styleSheet.cardSuppressed : null)}>
			<div
				className={css(styleSheet.cardHeader, !expanded ? styleSheet.cardHeaderCollapsed : null)}
				onClick={() => setExpanded(!expanded)}
				role='button'
				tabIndex={0}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						setExpanded(!expanded);
					}
				}}
			>
				<ScoreIndicator score={score.score} />
				<div className={css(styleSheet.cardContactInfo)}>
					<h3 className={css(styleSheet.cardContactName)}>
						<button type='button' className={css(styleSheet.cardContactLink)} onClick={handleOpenContactProfile}>
							{score.contactName ?? 'Unknown Contact'}
						</button>
					</h3>
					<div className={css(styleSheet.cardMeta)}>
						{score.companyName ? <span>{score.companyName}</span> : null}
						{daysText ? <span>{daysText}</span> : null}
					</div>
					{score.reasoning ? (
						<StreamdownLite styles={[styleSheet.cardReasoning]}>{stripCiteTags(score.reasoning)}</StreamdownLite>
					) : null}
					<span className={css(styleSheet.expandHint)}>{expanded ? 'Click to collapse' : 'Click to expand'}</span>
				</div>
				<div className={css(styleSheet.cardActions)} onClick={e => e.stopPropagation()}>
					{isSuppressed ? (
						<Button variant='outline' size='sm' onClick={handleUnsuppress} isLoading={updateMutation.isPending}>
							Restore
						</Button>
					) : (
						<>
							<div className={css(styleSheet.cardActionButtons)}>
								<Button variant='primary' size='sm' onClick={handleSendEmail}>
									Send Email
								</Button>
								<Button
									variant='outline'
									size='sm'
									onClick={() => acknowledgeMutation.mutate(score.id)}
									isLoading={acknowledgeMutation.isPending}
								>
									Snooze
								</Button>
							</div>
							<label className={css(styleSheet.skipCheckboxLabel)}>
								<Checkbox checked={false} onCheckedChange={handleSkip} />
								<span className={css(styleSheet.skipLabel)}>Don&apos;t suggest again</span>
							</label>
						</>
					)}
				</div>
			</div>
			{expanded ? (
				<div className={css(styleSheet.expandedSection)}>
					{score.reasoning ? (
						<div className={css(styleSheet.detailBlock)}>
							<div className={css(styleSheet.detailLabel)}>Reasoning</div>
							<StreamdownLite styles={[styleSheet.detailMarkdown]}>{stripCiteTags(score.reasoning)}</StreamdownLite>
						</div>
					) : null}
					{score.suggestedOutreach ? (
						<div className={css(styleSheet.detailBlock)}>
							<div className={css(styleSheet.detailLabel)}>Suggested Outreach</div>
							<StreamdownLite styles={[styleSheet.detailMarkdown]}>
								{stripCiteTags(score.suggestedOutreach)}
							</StreamdownLite>
						</div>
					) : null}
					{score.webFindings ? (
						<div className={css(styleSheet.detailBlock)}>
							<div className={css(styleSheet.detailLabel)}>Web Findings</div>
							<StreamdownLite styles={[styleSheet.detailMarkdown]}>{stripCiteTags(score.webFindings)}</StreamdownLite>
						</div>
					) : null}
					<div className={css(styleSheet.notesRow)}>
						<TextArea
							styles={[styleSheet.notesTextarea]}
							value={notes}
							onChange={e => {
								setNotes(e.target.value);
								setNotesDirty(true);
							}}
							placeholder='Add notes about this contact...'
						/>
						{notesDirty ? (
							<Button size='sm' onClick={handleSaveNotes} isLoading={updateMutation.isPending}>
								Save
							</Button>
						) : null}
					</div>
				</div>
			) : null}
		</div>
	);
};
